import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  BookOpen, Search, Plus, Trash2, Save, Download, RotateCcw, Info, Euro, Loader2,
  Zap, Droplets, Flame, Home, Building2, DoorOpen, Trees, Shield, Layers,
  PaintBucket, ChefHat, Bath, Thermometer, Hammer, Ruler, Video as VideoIcon,
  Upload, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '../utils/download';
import { VideoImportDialog } from '../components/VideoImportDialog';
import { NewItemBadge } from '../components/NewItemBadge';
import { getBordereau, updateBordereau } from '../offline/repo';
import { useAuth } from '../context/AuthContext';

const API = `${((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL)}/api`;

const UNIT_LABELS = { m2: 'm²', ml: 'ml', u: 'unité', forfait: 'forfait' };
const UNIT_OPTIONS = [
  { value: 'm2', label: 'm² (surface)' },
  { value: 'ml', label: 'ml (mètre linéaire)' },
  { value: 'u', label: 'unité / point' },
  { value: 'forfait', label: 'forfait' },
];

const CAT_ICONS = {
  'Electricite': Zap,
  'Plomberie': Droplets,
  'Chauffage / Climatisation': Flame,
  'Toiture / Charpente': Home,
  'Gros oeuvre / Structure': Building2,
  'Platrerie / Plaque de platre': Layers,
  'Isolation / Energetique': Thermometer,
  'Menuiseries interieures': DoorOpen,
  'Menuiseries exterieures': DoorOpen,
  'Revetements interieurs': Layers,
  'Peinture': PaintBucket,
  'Cuisine': ChefHat,
  'Salle de bain / WC': Bath,
  'Exterieur / Facade': Trees,
  'Conformite / Diagnostics': Shield,
  'Frais de chantier': Hammer,
};

const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const BordereauPage = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [categoriesOrder, setCategoriesOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);
  const [region, setRegion] = useState('');
  const [videoImportOpen, setVideoImportOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getBordereau();
      setItems(data.items || []);
      setCategoriesOrder(data.categories_order || []);
      setRegion(data.region || '');
      setDirty(false);
    } catch {
      if (navigator.onLine) toast.error('Erreur chargement bordereau');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Warn on unsaved changes when leaving the page
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
    setDirty(true);
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(it => it.id !== id));
    setDirty(true);
  };

  const addItemToCategory = (categorie) => {
    const newItem = {
      id: genId(),
      categorie,
      label: '',
      unite: 'm2',
      prix_mo: 0,
      notes: '',
    };
    setItems(prev => [...prev, newItem]);
    setDirty(true);
  };

  const addCategory = () => {
    const name = window.prompt('Nom du nouveau corps de métier :');
    if (!name) return;
    if (!categoriesOrder.includes(name)) setCategoriesOrder(prev => [...prev, name]);
    addItemToCategory(name);
  };

  const save = async () => {
    // Light validation
    const invalid = items.find(it => !it.label?.trim() || !it.categorie?.trim());
    if (invalid) {
      toast.error('Une ligne a un libellé ou une catégorie vide');
      return;
    }
    setSaving(true);
    try {
      const cleaned = items.map(it => ({
        ...it,
        prix_mo: parseFloat(it.prix_mo) || 0,
      }));
      await updateBordereau(cleaned);
      toast.success(navigator.onLine ? 'Bordereau enregistré' : 'Bordereau enregistré (hors-ligne, sera synchronisé)');
      setDirty(false);
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Réinitialiser tous les prix avec les valeurs moyennes Rians/Cher ? Vos modifications seront perdues.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bordereau/reset`, {}, { withCredentials: true });
      setItems(res.data.items || []);
      setDirty(false);
      toast.success('Bordereau réinitialisé');
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const syncWithMaster = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bordereau/sync-master`, {}, { withCredentials: true });
      const added = res.data?.added || 0;
      if (added > 0) {
        // Recharger le BPU pour afficher les nouvelles lignes
        const bpu = await axios.get(`${API}/bordereau`, { withCredentials: true });
        setItems(bpu.data.items || []);
        setDirty(false);
        toast.success(`✨ ${added} nouvelle${added > 1 ? 's' : ''} ligne${added > 1 ? 's' : ''} récupérée${added > 1 ? 's' : ''} depuis le BPU de référence`);
      } else {
        toast.info(res.data?.message || 'Votre BPU est déjà à jour');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSaving(false);
    }
  };

  const publishMaster = async () => {
    if (dirty) { toast.info('Enregistrez d\'abord vos modifications avant de publier'); return; }
    if (!window.confirm(
      'Publier votre BPU comme template de référence ?\n\n'
      + '• Tous les NOUVEAUX abonnés à partir de maintenant hériteront de cette version\n'
      + '• Les abonnés EXISTANTS pourront récupérer les nouveautés via le bouton "Mettre à jour" sur leur propre BPU\n'
      + '• Leurs prix personnalisés ne seront jamais écrasés'
    )) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/bordereau-master/publish`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'BPU publié comme template');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de publication');
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    if (dirty) { toast.info('Enregistrez d\'abord vos modifications'); return; }
    try {
      const res = await axios.get(`${API}/bordereau/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(res.data, 'bordereau-prix-home-mdb-2a.pdf', 'application/pdf');
      toast.success('PDF téléchargé');
    } catch (err) {
      toast.error('Export PDF échoué');
    }
  };

  // Group filtered items by category
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? items.filter(it => (it.label || '').toLowerCase().includes(q) || (it.categorie || '').toLowerCase().includes(q) || (it.notes || '').toLowerCase().includes(q))
      : items;
    const g = {};
    for (const it of filtered) {
      g[it.categorie] = g[it.categorie] || [];
      g[it.categorie].push(it);
    }
    // Order according to categoriesOrder, append extras at end
    const ordered = [];
    const seen = new Set();
    for (const cat of categoriesOrder) {
      if (g[cat]) { ordered.push([cat, g[cat]]); seen.add(cat); }
    }
    for (const cat of Object.keys(g)) {
      if (!seen.has(cat)) ordered.push([cat, g[cat]]);
    }
    return ordered;
  }, [items, search, categoriesOrder]);

  const totalLines = items.length;
  const avgPrice = totalLines ? items.reduce((s, it) => s + (parseFloat(it.prix_mo) || 0), 0) / totalLines : 0;

  return (
    <div className="space-y-4" data-testid="bordereau-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-emerald-500" />
            Bordereau de Prix Unitaire
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Prix MO + matériel (fourni-posé) · {region || 'Région indéfinie'} · à partager avec vos artisans
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
            onClick={syncWithMaster}
            disabled={saving}
            data-testid="sync-bpu-master"
            title="Récupère les nouvelles lignes du BPU de référence (sans écraser vos prix)"
          >
            <Upload className="w-4 h-4 mr-1 rotate-180" /> Mettre à jour
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
              onClick={publishMaster}
              disabled={saving}
              data-testid="publish-bpu-master"
              title="Publier votre BPU comme template de référence pour tous les nouveaux abonnés"
            >
              <Megaphone className="w-4 h-4 mr-1" /> Publier comme référence
            </Button>
          )}
          <Button
            variant="outline"
            className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-500/10"
            onClick={() => setVideoImportOpen(true)}
            data-testid="open-video-import"
          >
            <VideoIcon className="w-4 h-4 mr-1" /> Importer (vidéo / PDF / photo)
          </Button>
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={resetToDefaults} disabled={saving} data-testid="reset-bordereau">
            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
          <Button variant="outline" className="border-zinc-700 text-emerald-400" onClick={exportPdf} data-testid="export-bordereau-pdf">
            <Download className="w-4 h-4 mr-1" /> Export PDF
          </Button>
          <Button onClick={save} disabled={saving || !dirty} data-testid="save-bordereau" className="bg-emerald-600 hover:bg-emerald-500">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {dirty ? 'Enregistrer' : 'Enregistré'}
          </Button>
        </div>
      </div>

      {/* Stats + note */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-[11px] text-zinc-500 uppercase">Total lignes</div>
            <div className="text-xl font-bold text-white">{totalLines}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-[11px] text-zinc-500 uppercase">Corps de métier</div>
            <div className="text-xl font-bold text-white">{grouped.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-[11px] text-zinc-500 uppercase">Prix moyen MO</div>
            <div className="text-xl font-bold text-emerald-400 font-mono">{avgPrice.toFixed(0)} €</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-[11px] text-zinc-500 uppercase">Statut</div>
            <div className="text-sm font-semibold">
              {dirty
                ? <span className="text-amber-400">● Non enregistré</span>
                : <span className="text-emerald-400">● Synchronisé</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-xs text-blue-300">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>MO + matériel</strong> (€HT, fourni-posé). Saisissez le prix complet par unité, matériel inclus. Les prix indiqués sont des moyennes pour la région rurale Cher/Rians — ajustez-les selon vos négociations et vos achats matériel.
        </div>
      </div>

      {/* Search + add category */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un poste, une catégorie, une note..."
            className="pl-9 bg-zinc-900 border-zinc-800 text-white"
            data-testid="bordereau-search"
          />
        </div>
        <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={addCategory} data-testid="add-category">
          <Plus className="w-4 h-4 mr-1" /> Nouveau corps de métier
        </Button>
      </div>

      {/* Categories list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : grouped.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500 text-sm">
            Aucun poste ne correspond à « {search} »
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" defaultValue={grouped.slice(0, 3).map(([c]) => c)} className="space-y-2">
          {grouped.map(([cat, catItems]) => {
            const Icon = CAT_ICONS[cat] || Hammer;
            const minPrice = Math.min(...catItems.map(i => parseFloat(i.prix_mo) || 0));
            const maxPrice = Math.max(...catItems.map(i => parseFloat(i.prix_mo) || 0));
            return (
              <AccordionItem key={cat} value={cat} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-zinc-800/50" data-testid={`accordion-${cat}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Icon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-semibold text-white truncate">{cat}</div>
                      <div className="text-xs text-zinc-500">{catItems.length} poste{catItems.length > 1 ? 's' : ''} · {minPrice}€ → {maxPrice}€</div>
                    </div>
                    <Badge variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-400 text-[10px] mr-2">
                      {catItems.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  {/* Desktop table-style header */}
                  <div className="hidden md:grid grid-cols-[1fr,150px,90px,90px,1fr,40px] gap-2 px-2 py-1 text-[10px] uppercase text-zinc-500 font-semibold">
                    <div>Désignation</div>
                    <div>Catégorie</div>
                    <div>Unité</div>
                    <div className="text-right">MO + matériel (€)</div>
                    <div>Notes</div>
                    <div></div>
                  </div>
                  <div className="space-y-2 md:space-y-1">
                    {catItems.map(it => (
                      <div key={it.id} className="md:grid md:grid-cols-[1fr,150px,90px,90px,1fr,40px] md:gap-2 flex flex-col gap-2 bg-zinc-950/40 md:bg-transparent border md:border-0 border-zinc-800 rounded-md md:rounded-none p-2 md:p-0 hover:md:bg-zinc-800/30 transition-colors" data-testid={`bord-item-${it.id}`}>
                        <div className="relative">
                          <Input value={it.label}
                            onChange={(e) => updateItem(it.id, 'label', e.target.value)}
                            placeholder="Désignation"
                            className={`bg-zinc-950 border-zinc-800 text-white h-9 text-sm ${it.added_at ? 'pr-20' : ''}`}
                          />
                          {it.added_at && (
                            <NewItemBadge addedAt={it.added_at} className="absolute right-2 top-1/2 -translate-y-1/2" />
                          )}
                        </div>
                        <Select value={it.categorie || cat} onValueChange={(v) => updateItem(it.id, 'categorie', v)}>
                          <SelectTrigger
                            className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm"
                            data-testid={`category-select-${it.id}`}
                            title="Réaffecter cette ligne à une autre catégorie"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {(categoriesOrder.length ? categoriesOrder : Object.keys(CAT_ICONS)).map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={it.unite} onValueChange={(v) => updateItem(it.id, 'unite', v)}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="relative">
                          <Input type="number" step="0.01" value={it.prix_mo}
                            onChange={(e) => updateItem(it.id, 'prix_mo', e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm pr-6 font-mono text-right"
                          />
                          <Euro className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                        </div>
                        <Input value={it.notes || ''}
                          onChange={(e) => updateItem(it.id, 'notes', e.target.value)}
                          placeholder="Notes (optionnel)"
                          className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm"
                        />
                        <Button type="button" size="icon" variant="ghost"
                          className="h-9 w-9 text-zinc-500 hover:text-red-400 self-end md:self-auto"
                          onClick={() => removeItem(it.id)}
                          data-testid={`delete-item-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm"
                    className="mt-3 w-full border-zinc-700 border-dashed text-zinc-400 hover:text-white"
                    onClick={() => addItemToCategory(cat)}
                    data-testid={`add-item-${cat}`}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Ajouter une ligne
                  </Button>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <div className="text-xs text-zinc-600 text-center py-4 flex items-center justify-center gap-2">
        <Ruler className="w-3 h-3" />
        Astuce : exportez le PDF et partagez-le à vos artisans pour verrouiller les prix MO + matériel avant chantier.
      </div>

      <VideoImportDialog
        open={videoImportOpen}
        onOpenChange={setVideoImportOpen}
        onImported={fetchData}
      />
    </div>
  );
};
